import React, { useEffect, useState, useRef } from 'react'
import { Constants } from '@/assets'
import NextImage from 'next/image'
import Box from '@mui/material/Box'
import { useTranslation, Trans } from 'next-i18next'
import { EmtTypography, EmtButton, EmtIcon } from 'emt-components-v2'
import { useIsResponsive } from '@/hooks'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import AddressLocationForm from './AddressLocationForm'
import VariantImages from '@/assets/VariantImages'
import AlreadyAccountBox from './AlreadyAccountBox'
import AppRatingBox from './AppRatingBox'
import { Address } from '@/dataV3/Common/Address'
import { PARIS_CENTER } from '@/utils/search/ipGeoDefaults'

interface LocationStepComponentProps {
  onNext: () => void
  defaultAddress?: Address
}

const LocationStep: React.FC<LocationStepComponentProps> = ({ onNext, defaultAddress }) => {
  const { t: tp } = useTranslation('petsitter')
  const [isMobile] = useIsResponsive()

  const mapRef = useRef<HTMLDivElement | null>(null)
  const hasInitializedAddress = useRef(false)

  const address = useSignUpCallRequestStore((s) => s.address)
  const hasHydrated = useSignUpCallRequestStore((s) => s.hasHydrated)
  const setAddress = useSignUpCallRequestStore((s) => s.setAddress)
  const [valid, setValid] = useState<boolean>(!!address?.formatted)
  const mapAddress = address || defaultAddress || PARIS_CENTER

  useEffect(() => {
    if (!hasHydrated || hasInitializedAddress.current) return

    if (address) {
      hasInitializedAddress.current = true
      return
    }

    if (!defaultAddress) return

    hasInitializedAddress.current = true
    setAddress(defaultAddress)
    setValid(!!defaultAddress.formatted)
  }, [hasHydrated, address, defaultAddress, setAddress])

  const handleSubmit = async () => {
    onNext()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      <Box>
        <EmtTypography variant="h3" fontWeight="800" sx={{ fontSize: isMobile ? '24px' : '34px' }}>
          Où cherchez-vous un pet sitter ?
        </EmtTypography>
      </Box>

      {/* Flex container for AddressAutocomplete and ProfileMap */}
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: isMobile ? 'relative' : 'absolute',
            bottom: isMobile ? 'unset' : '16px',
            left: isMobile ? 'unset' : '16px',
            right: isMobile ? 'unset' : '16px',
            zIndex: 1,
            width: isMobile ? '100%' : 'calc(100% - 32px)',
          }}
        >
          <AddressLocationForm
            initialAddress={address}
            onChange={(addr, isValid) => {
              setAddress(addr)
              setValid(isValid)
            }}
          />
        </Box>
        {!isMobile && (
          <Box
            ref={mapRef}
            sx={{
              height: 240,
              borderRadius: '18px',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 0,
              '& .place-card': {
                display: 'none',
              },
            }}
          >
            <div style={{ height: '100%', width: '100%', position: 'relative' }}>
              {/* Iframe as map background */}
              <iframe
                id="googlemapiframe"
                key={mapAddress.latitude + mapAddress.longitude}
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  pointerEvents: 'none',
                  position: 'relative',
                  zIndex: 1,
                  width: '130%',
                  left: '-15%',
                  height: '130%',
                  top: '-15%',
                }}
                src={`https://www.google.com/maps/embed/v1/place?key=${Constants.gMapKey}&q=${mapAddress.latitude}+${mapAddress.longitude}+wsq+q&center=${mapAddress.latitude}+${mapAddress.longitude}&zoom=16`}
              />
              {/* Gradient overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 2,
                  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 65.29%, rgba(0, 0, 0, 0.2) 100%)',
                }}
              />
              {/* Pin image above gradient */}
              <NextImage
                style={{ zIndex: 9, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -90%)' }}
                src={VariantImages.PinSitter}
                width={46}
                alt="Map pin"
              />
            </div>
          </Box>
        )}
      </Box>

      <EmtButton fullWidth color="purple" size="large" variant="contained" disabled={!valid} onClick={handleSubmit}>
        Voir les prix
      </EmtButton>
      {isMobile && <AlreadyAccountBox />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '16px',
        }}
      >
        <EmtIcon
          className="emt-info-circle"
          sx={{
            color: '#F9F9F9',
            backgroundColor: '#4A4754',
            borderRadius: '50%',
            padding: '0px',
            fontSize: '20px',
            border: '1px solid #4A4754',
          }}
        />
        <EmtTypography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          <Trans
            t={tp}
            i18nKey={'search.testInfoBoxText'}
            components={{
              IconSkill: <EmtIcon sx={{ display: 'inline', color: '#0030cc' }} className="emt-knowledge" />,
              IconPsychological: <EmtIcon sx={{ display: 'inline', color: '#C80068' }} className="emt-psychology" />,
              strong: <strong />,
              BlueInseparables: (
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#0030cc',
                    fontWeight: '700',
                    lineHeight: '0',
                    height: '0px',
                    transform: 'translateY(2px)',
                  }}
                />
              ),
              PinkInseparables: (
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: '#C80068',
                    fontWeight: '700',
                    lineHeight: '0',
                    height: '0px',
                    transform: 'translateY(2px)',
                  }}
                />
              ),
            }}
          />
        </EmtTypography>
      </Box>
      {isMobile && <AppRatingBox />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <EmtTypography variant="body2" color="textSecondary" fontWeight="600" sx={{ textAlign: 'center' }}>
          Recommandé par les médias de confiance :
        </EmtTypography>
        <NextImage
          src={VariantImages.Medias2}
          alt="sitter footer"
          width={327}
          height={15}
          style={{ display: 'block', filter: 'grayscale(100%)' }}
        />
      </Box>
    </Box>
  )
}

export default LocationStep
